import { useState, useRef, useCallback } from 'react';
import { normalizeDateToISO } from '@/lib/dateUtils';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Plus,
  CheckCircle2,
  X,
  Camera,
  FileImage,
  FileText,
  Sparkles,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import type { SoloQueuePatient } from '@/hooks/useSoloDailyQueue';
import type { PreLoadedDoc } from '@/lib/templates/types';

interface StaffPreChartFormProps {
  onSave: (patient: Omit<SoloQueuePatient, 'id' | 'added_at'>) => void;
  onCancel: () => void;
}

interface ExtractedFile {
  name: string;
  docType: string;
  file: File;
}

const COMPLETE_AUDIT_PACKET_DOCS = [
  { name: 'synthetic-face-sheet-maya-thompson.pdf', docType: 'Face Sheet' },
  { name: 'synthetic-mri-left-knee-maya-thompson.pdf', docType: 'MRI Report' },
  { name: 'synthetic-pt-notes-8-weeks-maya-thompson.pdf', docType: 'PT Notes' },
  { name: 'synthetic-weight-bearing-xray-maya-thompson.pdf', docType: 'X-Ray Report' },
  { name: 'synthetic-clinic-note-failed-conservative-care.pdf', docType: 'Clinic Notes' },
] as const;

function createSyntheticFile(fileName: string): File {
  const body = [
    'Synthetic LINK audit packet document.',
    'This file is intentionally fake and contains no real PHI.',
    `Generated for ${fileName}.`,
  ].join('\n');

  return new File([body], fileName, { type: 'application/pdf' });
}

function getNextAppointmentTime(): string {
  const now = new Date();
  const nextHour = new Date(now);
  nextHour.setHours(now.getHours() + 1, 30, 0, 0);
  return nextHour.toTimeString().slice(0, 5);
}

export function StaffPreChartForm({ onSave, onCancel }: StaffPreChartFormProps) {
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [sex, setSex] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [procedure, setProcedure] = useState('');
  const [facility, setFacility] = useState('');
  const [payer, setPayer] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<ExtractedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);

  const runAIExtraction = useCallback(async (file: File) => {
    const { inferMimeType: inferMime, getExtractionStrategy, extractTextFromFile, fileToBase64: toBase64 } = await import('@/lib/fileExtraction');
    setIsExtracting(true);
    try {
      const mimeType = inferMime(file);
      const strategy = getExtractionStrategy(mimeType);

      if (strategy === 'unsupported') {
        toast.error('Unsupported file type', { description: 'Please upload a PDF, DOCX, TXT, or image file.' });
        return;
      }

      let requestBody: Record<string, unknown>;

      if (strategy === 'vision') {
        const base64 = await toBase64(file);
        requestBody = { imageBase64: base64, mimeType };
      } else {
        const rawText = await extractTextFromFile(file, mimeType);
        if (!rawText.trim()) {
          toast.error('No text found', { description: 'The document appears empty. Try a different file.' });
          return;
        }
        requestBody = { rawText };
      }

      const { data, error } = await supabase.functions.invoke('extract-patient-universal', {
        body: requestBody,
      });

      if (error) {
        console.error('OCR extraction error:', error);
        toast.error('AI extraction failed', { description: 'You can fill in the fields manually.' });
        return;
      }

      if (!data) return;

      const filled = new Set<string>();

      // Bind extracted values to form state
      if (data.patient_name) {
        setName(data.patient_name);
        filled.add('name');
      }
      if (data.patient_dob) {
        // Normalize date to YYYY-MM-DD for input[type=date]
        const parsed = normalizeDateToISO(data.patient_dob);
        if (parsed) { setDob(parsed); filled.add('dob'); }
      }
      if (data.patient_sex) {
        const normalizedSex = data.patient_sex.toLowerCase();
        if (normalizedSex === 'male' || normalizedSex === 'female') {
          setSex(normalizedSex);
          filled.add('sex');
        }
      }
      if (data.payer_name) {
        setPayer(data.payer_name);
        filled.add('payer');
      }
      if (data.scheduled_procedure) {
        setProcedure(data.scheduled_procedure);
        filled.add('procedure');
      }
      if (data.facility_name) {
        setFacility(data.facility_name);
        filled.add('facility');
      }

      setAutoFilledFields(filled);

      if (filled.size > 0) {
        toast.success(`AI extracted ${filled.size} field(s)`, {
          description: 'Review the auto-filled data below and fill any gaps.',
          icon: <Sparkles className="w-4 h-4" />,
        });
      } else {
        toast.info('No demographics found in document', {
          description: 'Please fill in the fields manually.',
        });
      }
    } catch (err) {
      console.error('AI extraction error:', err);
      toast.error('AI extraction failed');
    } finally {
      setIsExtracting(false);
    }
  }, []);

  

  const classifyFile = (fileName: string): string => {
    const lower = fileName.toLowerCase();
    if (lower.includes('mri')) return 'MRI Report';
    if (lower.includes('pt') || lower.includes('therapy')) return 'PT Notes';
    if (lower.includes('xray') || lower.includes('x-ray')) return 'X-Ray Report';
    if (lower.includes('face') || lower.includes('sheet')) return 'Face Sheet';
    if (lower.includes('note') || lower.includes('clinic')) return 'Clinic Notes';
    return 'Clinical Document';
  };

  const handleFileAdd = useCallback(async (files: File[]) => {
    const newFiles: ExtractedFile[] = files.map(f => ({
      name: f.name,
      docType: classifyFile(f.name),
      file: f,
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);

    // Trigger AI extraction on the first supported file
    const { inferMimeType: inferMime, getExtractionStrategy } = await import('@/lib/fileExtraction');
    const extractable = files.find(f => {
      const mime = inferMime(f);
      return getExtractionStrategy(mime) !== 'unsupported';
    });
    if (extractable && !isExtracting) {
      await runAIExtraction(extractable);
    }
  }, [isExtracting, runAIExtraction]);

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileAdd(Array.from(e.dataTransfer.files));
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleFileAdd(Array.from(e.target.files));
    if (e.target) e.target.value = '';
  };

  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const loadCompleteAuditPacket = () => {
    setName('Thompson, Maya');
    setDob('1978-04-14');
    setSex('female');
    setAppointmentTime(getNextAppointmentTime());
    setProcedure('Left Knee Arthroscopy with Partial Medial Meniscectomy');
    setFacility('Beacon Orthopaedics / St. Elizabeth Florence');
    setPayer('Anthem BCBS KY');
    setUploadedFiles(COMPLETE_AUDIT_PACKET_DOCS.map(doc => ({
      name: doc.name,
      docType: doc.docType,
      file: createSyntheticFile(doc.name),
    })));
    setAutoFilledFields(new Set(['name', 'dob', 'sex', 'payer', 'procedure', 'facility']));

    toast.success('Synthetic audit packet loaded', {
      description: 'Fake patient + face sheet, MRI, PT notes, X-ray, and clinic note are ready to save.',
      icon: <Sparkles className="w-4 h-4" />,
    });
  };

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error('Patient name is required');
      return;
    }
    if (!appointmentTime) {
      toast.error('Appointment time is required');
      return;
    }

    const docs: PreLoadedDoc[] = uploadedFiles.map(f => ({
      type: f.docType,
      status: 'verified' as const,
      uploaded_by: 'Staff - Pre-Chart',
      uploaded_at: new Date().toISOString(),
    }));

    onSave({
      patient_name: name.trim(),
      patient_dob: dob || null,
      patient_sex: sex || null,
      appointment_time: new Date(`${new Date().toISOString().split('T')[0]}T${appointmentTime}`).toISOString(),
      planned_procedure: procedure.trim(),
      facility_name: facility.trim() || null,
      payer_name: payer.trim() || null,
      pre_loaded_docs: docs,
      added_by: 'Staff - Pre-Chart',
    });

    toast.success('Patient added to schedule', {
      description: `${name.trim()} at ${appointmentTime}${docs.length > 0 ? ` with ${docs.length} doc(s)` : ''}`,
    });
  };

  const fieldHighlight = (field: string) =>
    autoFilledFields.has(field) ? 'ring-2 ring-primary/30 bg-primary/5' : 'bg-card';

  return (
    <Card className="border-amber-200 bg-amber-50/30 shadow-sm">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Plus className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-foreground">Add Patient to Today's Schedule</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-7 gap-1.5 text-[11px] border-primary/30 text-primary hover:bg-primary/5"
              onClick={loadCompleteAuditPacket}
              disabled={isExtracting}
              type="button"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Load Fake Audit Packet
            </Button>
            <Button variant="ghost" size="icon" className="w-7 h-7" onClick={onCancel}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* === CLINICAL EVIDENCE DROPZONE — TOP OF FORM === */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Clinical Evidence
            </label>
            <Badge variant="secondary" className="text-[9px] gap-1">
              <Sparkles className="w-2.5 h-2.5" />
              AI Auto-Fill
            </Badge>
          </div>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isExtracting && fileInputRef.current?.click()}
            className={cn(
              'relative rounded-xl border-2 border-dashed transition-all duration-200',
              isExtracting
                ? 'border-primary bg-primary/5 cursor-wait'
                : isDragging
                  ? 'border-amber-500 bg-amber-50/80 scale-[1.01] cursor-copy'
                  : 'border-amber-300 bg-card hover:border-amber-500 hover:bg-amber-50/50 cursor-pointer',
            )}
          >
            <input ref={fileInputRef} type="file" className="hidden" accept="image/*,.pdf,.doc,.docx,.txt,.csv,.rtf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,text/plain,text/csv,application/rtf" multiple onChange={handleFileSelect} />
            <div className="flex flex-col items-center gap-2 py-6 px-4 text-center">
              {isExtracting ? (
                <>
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                  <p className="text-xs font-medium text-primary">Analyzing document & extracting patient data…</p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><Camera className="w-4 h-4 text-amber-600" /></div>
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><FileImage className="w-4 h-4 text-amber-600" /></div>
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center"><FileText className="w-4 h-4 text-amber-600" /></div>
                  </div>
                  <p className="text-xs font-medium text-foreground">
                    {isDragging ? 'Drop files here…' : 'Drop a face sheet, MRI, or clinic note to auto-fill patient data'}
                  </p>
                  <p className="text-[10px] text-muted-foreground">PDF & image files supported</p>
                </>
              )}
            </div>
          </div>

          {uploadedFiles.length > 0 && (
            <div className="space-y-1.5">
              {uploadedFiles.map((f, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg border bg-card text-xs">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                  <span className="flex-1 truncate">{f.name}</span>
                  <Badge variant="outline" className="text-[9px]">{f.docType}</Badge>
                  <Button variant="ghost" size="icon" className="w-5 h-5" onClick={() => removeFile(i)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {autoFilledFields.size > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/5 border border-primary/20 text-xs text-primary">
            <Sparkles className="w-3.5 h-3.5 shrink-0" />
            <span>AI auto-filled {autoFilledFields.size} field(s) — review and fill any gaps below.</span>
          </div>
        )}

        <Separator />

        {/* === FORM FIELDS (now verification step) === */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2 space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Patient Name <span className="text-destructive">*</span>
            </label>
            <Input placeholder="Last, First" value={name} onChange={e => { setName(e.target.value); setAutoFilledFields(prev => { const n = new Set(prev); n.delete('name'); return n; }); }} className={cn('h-9', fieldHighlight('name'))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date of Birth</label>
            <Input type="date" value={dob} onChange={e => { setDob(e.target.value); setAutoFilledFields(prev => { const n = new Set(prev); n.delete('dob'); return n; }); }} className={cn('h-9', fieldHighlight('dob'))} />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Appointment Time <span className="text-destructive">*</span>
            </label>
            <Input type="time" value={appointmentTime} onChange={e => setAppointmentTime(e.target.value)} className="h-9 bg-card" />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Sex</label>
            <div className="flex gap-2">
              <Button variant={sex === 'male' ? 'default' : 'outline'} size="sm" onClick={() => setSex('male')} className={cn('flex-1 h-9', autoFilledFields.has('sex') && sex === 'male' ? 'ring-2 ring-primary/30' : '')}>Male</Button>
              <Button variant={sex === 'female' ? 'default' : 'outline'} size="sm" onClick={() => setSex('female')} className={cn('flex-1 h-9', autoFilledFields.has('sex') && sex === 'female' ? 'ring-2 ring-primary/30' : '')}>Female</Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Payer</label>
            <Input placeholder="e.g., UnitedHealthcare" value={payer} onChange={e => { setPayer(e.target.value); setAutoFilledFields(prev => { const n = new Set(prev); n.delete('payer'); return n; }); }} className={cn('h-9', fieldHighlight('payer'))} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Expected Procedure</label>
            <Input placeholder="e.g., Right Knee Arthroscopy" value={procedure} onChange={e => { setProcedure(e.target.value); setAutoFilledFields(prev => { const n = new Set(prev); n.delete('procedure'); return n; }); }} className={cn('h-9', fieldHighlight('procedure'))} />
          </div>
          <div className="sm:col-span-2 space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Facility</label>
            <Input placeholder="e.g., Ohio Surgery Center" value={facility} onChange={e => { setFacility(e.target.value); setAutoFilledFields(prev => { const n = new Set(prev); n.delete('facility'); return n; }); }} className={cn('h-9', fieldHighlight('facility'))} />
          </div>
        </div>

        <Separator />

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={!name.trim() || !appointmentTime || isExtracting}>
            <Plus className="w-4 h-4 mr-1" />
            Save to Schedule
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
